import ReactGA from 'react-ga4';

// Sends an event to Google Analytics (GA4, initialised in App.js). The
// dataLayer pushes below only reach Tag Manager, and this site runs no Tag
// Manager container (just the Ads tag), so the events that matter go to GA4
// here too. Never throws: analytics must not break a page.
const ga = (name, params = {}) => {
  try {
    ReactGA.event(name, params);
  } catch (_err) {
    // GA not initialised (tests, blocked) — ignore
  }
};

export const logEvent = (category, action, label) => {
  ga('button_click', { event_category: category, event_action: action, event_label: label });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'button_click',
    event_category: category,
    event_action: action,
    event_label: label,
    page_location: window.location.href,
  });
};

const ATTRIBUTION_KEY = 'af_attribution';
const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
];

// Capture ad/campaign params on first landing and persist them for the
// session, so a lead submitted later (after scrolling/navigating) is still
// attributable to the ad that drove it. First-touch wins — we don't overwrite
// an existing capture within the same session.
export const captureAttribution = () => {
  try {
    if (sessionStorage.getItem(ATTRIBUTION_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    ATTRIBUTION_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) captured[key] = value;
    });
    if (Object.keys(captured).length === 0) return;
    captured.landing_page = window.location.pathname;
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(captured));
  } catch (_err) {
    // sessionStorage unavailable (private mode / SSR) — attribution is best-effort
  }
};

export const getAttribution = () => {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_err) {
    return {};
  }
};

// Fire a single lead event to the dataLayer. GTM forwards this to GA4, Google
// Ads, and (once installed) the Meta Pixel, so every platform's conversion is
// driven off one source of truth.
export const trackLead = (details = {}) => {
  ga('generate_lead', { ...getAttribution(), ...details });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'generate_lead',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};

// Free-tool (macro / meal-plan) lead-magnet signups. Deliberately a DISTINCT
// event from `generate_lead` so these top-of-funnel emails are measurable in
// GA/Ads without inflating the consult-conversion count. Wire `macro_lead` up in
// GTM to whatever GA4 event / Ads action you want.
export const trackMacroLead = (details = {}) => {
  ga('macro_lead', { ...getAttribution(), ...details });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'macro_lead',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};

// 6-Week Challenge signups. Distinct event so challenge registrations are
// measurable in GA/Ads without mixing into the consult or macro-lead counts.
export const trackChallengeSignup = (details = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'challenge_signup',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};

// Dumbbells-Only starter (RESET Starter) PDF downloads. Distinct event so this
// top-of-funnel lead magnet is measurable on its own in GA/Ads.
export const trackResetStarter = (details = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'reset_starter',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};

// Meal prep "meals for your macros": `meal_macros_set` when someone sets
// their macros ({ source: 'typed' | 'calculated' | 'saved', page }) and
// `meal_macros_fill` when they fill an order with their best fits
// ({ mode, count, page }). Wire them up in GTM to see whether it's used.
export const trackMealMacros = (event, details = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...details,
    page_location: window.location.href,
  });
};

// 1-on-1 coaching applications. Distinct event; pass { qualified } so you can
// split qualified applicants from nurture in GA/Ads.
export const trackCoachingApplication = (details = {}) => {
  ga('coaching_application', { ...getAttribution(), ...details });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'coaching_application',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};

// /online: which button was clicked and where ("hero" / "nav" / "coach" /
// "pricing" / "closing"; "apply" or "checkout"), so GA4 shows how many
// visits turn into applications and straight checkouts.
export const trackOnlineCta = (where, action) => {
  ga('online_cta', { where, action, ...getAttribution() });
};

// /apply: they got past the contact step and are answering the questions.
export const trackApplicationStarted = (tier = 'online') => {
  ga('application_started', { tier, ...getAttribution() });
};

// Home page: which card or button was clicked ("hero" / "paths" / "closing";
// the card's key or "contact" / "apply").
export const trackHomeCta = (where, action) => {
  ga('home_cta', { where, action, ...getAttribution() });
};

// /links: which card was tapped ("coaching" / "reset" / "studio" / "free" /
// "live"), so GA4 shows which link people actually use.
export const trackLinksClick = (card) => {
  ga('links_click', { card, ...getAttribution() });
};

// /free: someone asked for the freebies ({ freebies: 'starter,meal_plan' }).
export const trackFreebies = (details = {}) => {
  ga('free_signup', { ...getAttribution(), ...details });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'free_signup',
    ...getAttribution(),
    ...details,
    page_location: window.location.href,
  });
};
